'use client';

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faUserDoctor } from '@fortawesome/pro-solid-svg-icons';
import {
  faClipboardList,
  faSearch,
} from '@fortawesome/pro-regular-svg-icons';
import { EmptyState, PulseButton, CircularProgress } from 'src/components/PulseUI';
import {
  StrategyResultItem,
  StrategyResultItemSkeleton,
  StrategySortDropdown,
} from './components';
import { useDebounce } from 'use-debounce';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CreateRouteFromResultsButton } from 'src/components/Routes/components/CreateRouteFromResultsButton';
import useStrategies from 'src/hooks/strategy/useStrategies';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import useSelectAll from 'src/hooks/strategy/useSelectAll';
import { LOCATION } from 'src/hooks/filters/constants';
import { useGetAllStrategiesMembersListInfiniteQuery } from 'src/api/redux/strategy/allStrategiesApi';
import { Checkbox } from 'src/components';
import { useSite } from 'src/context/providers/SiteProvider';
import { useLogging } from 'src/context/logging';
import {
  useAppDispatch,
  setNavList,
  appendItems,
  clearNav,
} from 'src/redux';
import type { StrategyMetric } from 'src/api/redux/strategy/types';
import { resolveMetricStatSource } from 'src/utils/strategyHelpers';
import type {
  StrategySortParams,
  StrategySortValues,
} from 'src/hooks/strategy/useStrategyMetricSort';

interface StrategyMapResultsProps {
  search: string;
  /** Sort state owned by the map (`useStrategyMetricSort`). */
  sortValues: StrategySortValues;
  sortParams: StrategySortParams;
  /** False until a metric default is known; the request waits for it. */
  sortReady: boolean;
  onSortChange: (values: StrategySortValues) => void;
  /** Per-strategy metrics of the one selected chain, for the metric sort options. */
  strategyMetrics?: StrategyMetric[];
  setShowAddToListDialog: (show: boolean) => void;
  /** Reports "select all" resolution state up so the map's other add entry
   * points (e.g. marker popups) can share the same guard. */
  onResolvingChange?: (resolving: boolean) => void;
}

const StrategyMapResults = ({
  search,
  sortValues,
  sortParams,
  sortReady,
  onSortChange,
  strategyMetrics,
  setShowAddToListDialog,
  onResolvingChange,
}: StrategyMapResultsProps) => {
  const log = useLogging();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [debouncedSearch] = useDebounce(search, 500);
  const {
    boundedTerritory,
    bounds,
    selectedTerritory,
    setSelectedProvider,
    selectedCities,
    selectedStates,
    selectedSpecialties,
    selectedSocTypes,
    selectedSocSubtypes,
    selectedStatuses,
    selectedProviderLists,
    selectedChainIds,
    setActiveMarker,
  } = useStrategies(LOCATION.STRATEGY_CENTER);

  const { useWebRouting, showEncountersTotals } = useFlags();
  const { closeAllButCurrent } = useDrawer();
  const { dispatch } = useSite();
  const appDispatch = useAppDispatch();

  const queryParams = useMemo(
    () => ({
      chain_id: selectedChainIds,
      territory_id: selectedTerritory?.id,
      bounding_territory: boundedTerritory?.id,
      q: debouncedSearch || undefined,
      pageSize: 50,
      city: selectedCities,
      state: selectedStates,
      specialty: selectedSpecialties,
      soc_type: selectedSocTypes,
      soc_subtype: selectedSocSubtypes,
      crm_status: selectedStatuses,
      provider_list: selectedProviderLists,
      ...sortParams,
      ...bounds,
    }),
    [
      selectedChainIds,
      boundedTerritory?.id,
      selectedTerritory?.id,
      debouncedSearch,
      selectedCities,
      selectedStates,
      selectedSpecialties,
      selectedSocTypes,
      selectedSocSubtypes,
      selectedStatuses,
      selectedProviderLists,
      sortParams,
      bounds,
    ]
  );

  // Wait for the metric default so the first request already carries the sort.
  const skipQuery = selectedChainIds.length === 0 || !sortReady;

  // Each row shows the sorted metric (else the first one). With several chains the
  // metrics don't align across strategies, so there is no stat.
  const metricStat = useMemo(
    () =>
      selectedChainIds.length > 1
        ? null
        : resolveMetricStatSource(
            strategyMetrics,
            sortValues.sort_metric,
            !!showEncountersTotals
          ),
    [
      selectedChainIds.length,
      strategyMetrics,
      sortValues.sort_metric,
      showEncountersTotals,
    ]
  );

  const {
    data: listMembersData,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isError,
    error,
    fetchNextPage,
  } = useGetAllStrategiesMembersListInfiniteQuery(queryParams, {
    skip: skipQuery,
  });

  // A skipped query reports `isFetching: false`, so the pending sort is loading too;
  // otherwise the panel would flash "No Opportunities Found" until the metrics land.
  const isLoadingList =
    selectedChainIds.length > 0 && (isFetching || !sortReady);

  // Flatten all pages, normalize rows, and deduplicate by provider_id
  // (offset-based pagination can return overlapping results across pages)
  const allResults = useMemo(() => {
    const seen = new Set<string>();
    return (listMembersData?.pages ?? []).flatMap((p) =>
      p.results.reduce<any[]>((acc, r) => {
        const pid = r.provider_id || r.id;
        if (!seen.has(pid)) {
          seen.add(pid);
          acc.push({ ...r, provider_id: pid });
        }
        return acc;
      }, [])
    );
  }, [listMembersData]);

  const totalCount = listMembersData?.pages?.[0]?.count ?? 0;
  const canCreateRoute = !!useWebRouting && !skipQuery && totalCount > 0;
  const chainsMetadata = listMembersData?.pages?.[0]?.chains ?? {};
  const hasNextPage = !!listMembersData?.pages?.[listMembersData.pages.length - 1]?.next;

  const {
    effectiveProviders,
    effectiveCount,
    isHeaderChecked,
    isResolvingSelection,
    handleToggleSelectAll,
    resetSelection,
    showBanner,
    bannerState,
    handleSelectAllOpportunities,
    handleToggleProvider,
    loadedCount,
  } = useSelectAll(LOCATION.STRATEGY_CENTER, allResults, totalCount, queryParams, hasNextPage);

  const selectedProviderSet = useMemo(
    () => new Set(effectiveProviders.map((p) => p.provider_id)),
    [effectiveProviders]
  );

  // Surface "select all" resolution up to the map so every add entry point
  // (marker popups + the parent modal) can block until the full set is ready.
  useEffect(() => {
    onResolvingChange?.(isResolvingSelection);
  }, [isResolvingSelection, onResolvingChange]);

  const listMembers = useMemo(
    () => ({
      results: allResults,
      count: totalCount,
    }),
    [allResults, totalCount]
  );

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isFetching || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight - 200) {
      fetchNextPage();
    }
  }, [isFetching, hasNextPage, fetchNextPage]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Scroll to top when query params change
  useEffect(() => {
    scrollContainerRef.current?.scrollTo(0, 0);
  }, [queryParams]);

  // Keep nav slice in sync when new pages load
  useEffect(() => {
    if (allResults.length > 0) {
      const navItems = allResults.map((r) => ({
        id: r.provider_id,
        type: r.type || null,
      }));
      appDispatch(appendItems(navItems));
    }
  }, [allResults.length, appDispatch]);

  // Clear nav and selection when query params change (filter/sort reset)
  const queryParamsRef = useRef(queryParams);
  useEffect(() => {
    if (queryParamsRef.current !== queryParams) {
      queryParamsRef.current = queryParams;
      appDispatch(clearNav());
      resetSelection();
    }
  }, [queryParams, appDispatch, resetSelection]);

  const handleAddToLists = () => {
    setShowAddToListDialog(true);
  };

  const handleSelectProvider = (provider: MedScout.Opportunity) => {
    setSelectedProvider(provider);
    dispatch({
      type: 'SET_CURRENT_PROVIDER',
      payload: { id: provider.provider_id, type: provider.type || null },
    });

    // Set up drawer navigation
    const navItems = allResults.map((r) => ({
      id: r.provider_id,
      type: r.type || null,
    }));
    const clickedIndex = navItems.findIndex(
      (item) => item.id === provider.provider_id
    );
    appDispatch(
      setNavList({
        items: navItems,
        currentIndex: clickedIndex >= 0 ? clickedIndex : 0,
        totalCount,
        source: 'strategy-map',
      })
    );

    closeAllButCurrent('profile');
  };

  const handleSortChange = (values: StrategySortValues) => {
    onSortChange(values);
    log.event('Strategy map results sorted', {
      source: 'StrategyMapResults',
      sortField: values.sort_metric ?? values.sort,
      sortOrder: values.order,
    });
  };

  return (
    <div data-testid="strategy-map-results" className="w-96 h-full bg-white z-10 pointer-events-auto " role="region" aria-label="Strategy map results">
      <div className="h-full inline-flex flex-col  items-start overflow-hidden">
        <div className="w-96 flex-1 bg-white flex flex-col  items-start overflow-hidden">
          <div className="self-stretch p-4 bg-white flex flex-col  items-start gap-3">
            <div className="self-stretch inline-flex justify-between items-center">
              <div className="flex-1 flex items-center gap-1">
                <div className="w-7 h-7 flex items-center justify-center">
                  <Icon icon={faUserDoctor} className="text-primary" />
                </div>
                <div className="text-primary text-lg font-bold font-['Inter'] leading-7 flex items-center gap-2">
                  {isLoadingList ? (
                    <>
                      <div className="h-5 w-12 bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200 rounded overflow-hidden relative inline-block">
                        <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
                      </div>
                      <span>Opportunities</span>
                    </>
                  ) : (
                    `${listMembers?.count || 0} Opportunities`
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* Results Divider Header */}
          <div className="self-stretch h-10 p-4 bg-neutral-50 border-t border-b border-neutral-200 inline-flex justify-between items-center">
            <div className="flex-1 flex  items-center gap-2.5">
              <div className="flex  items-center gap-2.5">
                <Checkbox
                  aria-label="Select all providers"
                  data-testid="select-all-checkbox"
                  checked={isHeaderChecked}
                  onChange={handleToggleSelectAll}
                  size="sm"
                />
              </div>
              <div className="text-center  text-secondary text-sm font-normal font-['Inter'] leading-tight">
                Select All
              </div>
            </div>
            <StrategySortDropdown
              strategyMetrics={strategyMetrics}
              isMultiChain={selectedChainIds.length > 1}
              value={sortValues}
              onChange={handleSortChange}
            />
          </div>
          {/* Select All Banner */}
          {showBanner && (
            <div
              className="self-stretch px-4 py-2 bg-blue-50 border-b border-blue-200 text-sm text-secondary text-center"
              data-testid="select-all-banner"
            >
              {bannerState === 'page-selected' ? (
                <>
                  {loadedCount} of {totalCount} opportunities selected.{' '}
                  <button
                    onClick={handleSelectAllOpportunities}
                    className="font-semibold text-sky-700 hover:text-sky-900 cursor-pointer"
                    data-testid="select-all-opportunities-link"
                  >
                    Select all {totalCount}
                  </button>
                </>
              ) : (
                <>
                  All {totalCount} opportunities are selected.{' '}
                  <button
                    onClick={resetSelection}
                    className="font-semibold text-sky-700 hover:text-sky-900 cursor-pointer"
                    data-testid="clear-selection-link"
                  >
                    Clear selection
                  </button>
                </>
              )}
            </div>
          )}
          {/* Results List Content */}
          <div
            ref={scrollContainerRef}
            className="self-stretch flex-1 relative
          bg-background-level2 flex flex-col justify-start items-start gap-px overflow-y-auto max-h-[calc(100vh-200px)]"
          >
            {selectedChainIds.length === 0 ? (
              <EmptyState
                title="Select a Strategy"
                description="Choose a strategy from the dropdown to start viewing opportunities and insights."
                icon={
                  <Icon
                    icon={faClipboardList}
                    className="text-sky-500 text-4xl"
                  />
                }
                className="flex-1 border-none bg-transparent justify-center"
                size="md"
              />
            ) : isError ? (
              <EmptyState
                title="Error Loading Opportunities"
                description={
                  (error as any)?.data?.details ||
                  (error as any)?.data?.message ||
                  'An error occurred while loading opportunities. Please try again later.'
                }
                icon={
                  <Icon icon={faSearch} className="text-red-500 text-4xl" />
                }
                className="flex-1 border-none bg-transparent justify-center"
                size="md"
              />
            ) : isLoadingList && !isFetchingNextPage ? (
              <>
                {[...Array(8)].map((_, index) => (
                  <StrategyResultItemSkeleton key={`skeleton-${index}`} />
                ))}
              </>
            ) : !listMembers?.results?.length ? (
              <EmptyState
                title="No Opportunities Found"
                description="There are no opportunities matching your current selection. Try adjusting your strategy, territory, or filters."
                icon={
                  <Icon icon={faSearch} className="text-amber-500 text-4xl" />
                }
                className="flex-1 border-none bg-transparent justify-center"
                size="md"
              />
            ) : (
              listMembers?.results?.map((provider, index) => (
                <StrategyResultItem
                  key={`${provider.id}-${index}`}
                  provider={provider as any}
                  onSelect={(provider) => handleSelectProvider(provider)}
                  setActiveMarker={setActiveMarker}
                  chainsMetadata={chainsMetadata}
                  isSelected={selectedProviderSet.has(provider.provider_id)}
                  onToggleSelect={handleToggleProvider}
                  metricStat={metricStat}
                  isMetricSorted={sortValues.sort === 'metric'}
                />
              ))
            )}
            {isFetchingNextPage && (
              <div className="flex items-center justify-center py-4 w-full">
                <CircularProgress size="sm" color="neutral" />
              </div>
            )}
          </div>
          {/* Create Route (from visible results) and Add to List buttons */}
          {(effectiveCount > 0 || canCreateRoute) && (
            <div className="self-stretch h-16 p-4 border-t border-neutral-200 flex gap-2 justify-between items-center bg-white">
              {canCreateRoute && (
                <CreateRouteFromResultsButton
                  queryParams={queryParams}
                  className="w-full rounded-full"
                />
              )}
              {effectiveCount > 0 && (
                <PulseButton
                  variant="solid"
                  size="md"
                  className="w-full rounded-full bg-sky-600 text-white"
                  onClick={handleAddToLists}
                  disabled={isResolvingSelection}
                  aria-busy={isResolvingSelection}
                  aria-label={
                    isResolvingSelection
                      ? 'Gathering selected providers'
                      : `Add ${effectiveCount} providers to list`
                  }
                  data-testid="add-to-list-button"
                >
                  {isResolvingSelection
                    ? 'Gathering selection…'
                    : `Add ${effectiveCount} to List`}
                </PulseButton>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StrategyMapResults;
